/*
 * PollBlock — homepage active poll block.
 *
 * Renders the currently active poll marked with `show_on_homepage=true` for
 * the visitor's selected province (with a global fallback so smaller
 * provinces still see one). The block itself is a compact, embeddable
 * variant of the Anketler page's PollCard.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useProvince } from "@/contexts/ProvinceContext";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { BarChart2, CheckCircle } from "lucide-react";

export default function PollBlock() {
  const { selected } = useProvince();
  const { user } = useAuth();
  const [poll, setPoll] = useState(null);
  const [voted, setVoted] = useState(false);
  const [myOpt, setMyOpt] = useState(null);

  useEffect(() => {
    const q = selected?.id ? `?province_id=${selected.id}` : "";
    api.get(`/polls${q}`).then((r) => {
      const list = r.data || [];
      const chosen =
        list.find((p) => p.is_active && p.show_on_homepage) ||
        list.find((p) => p.is_active) || null;
      setPoll(chosen);
      setVoted(false);
      setMyOpt(null);
    }).catch(() => setPoll(null));
  }, [selected?.id]);

  useEffect(() => {
    if (!user || !poll?.id) return;
    api.get(`/polls/${poll.id}/my-vote`).then((r) => {
      setVoted(!!r.data.voted);
      setMyOpt(r.data.option_id);
    }).catch(() => {});
  }, [user, poll?.id]);

  const total = poll?.total_votes || 0;
  const showResults = voted || poll?.show_results === false ? voted : voted; // always show after vote
  const hasImages = useMemo(
    () => (poll?.options || []).some((o) => o.image),
    [poll],
  );

  const vote = async (optionId) => {
    if (!user) return toast.error("Oy vermek için giriş yapmalısınız");
    try {
      const r = await api.post(`/polls/${poll.id}/vote`, { option_id: optionId });
      setPoll(r.data);
      setVoted(true);
      setMyOpt(optionId);
      toast.success("Oyunuz alındı");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Hata oluştu");
    }
  };

  if (!poll) return null;

  return (
    <div className="w-full bg-white rounded-lg border border-gray-100 p-5" data-testid="home-poll-block">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <BarChart2 className="w-5 h-5 text-[#e11d2a]" />
          <h3 className="text-lg font-black">ANKET</h3>
          <span className="text-[10px] uppercase font-bold tracking-wide bg-[#e11d2a] text-white px-2 py-0.5 rounded">
            Aktif
          </span>
        </div>
        <Link
          to="/anketler"
          data-testid="home-poll-all-link"
          className="text-xs font-semibold text-[#e11d2a] hover:underline"
        >
          Tüm Anketler →
        </Link>
      </div>

      {poll.image && (
        <div className="aspect-[16/6] rounded-md overflow-hidden mb-3 bg-gray-100">
          <img src={poll.image} alt={poll.title} className="w-full h-full object-cover" />
        </div>
      )}

      <h4 className="text-base md:text-lg font-bold leading-snug" data-testid="home-poll-title">
        {poll.title}
      </h4>
      {poll.description && <p className="text-xs text-gray-600 mt-1">{poll.description}</p>}

      <div className={`mt-3 grid ${hasImages ? "grid-cols-2 md:grid-cols-3" : "grid-cols-1"} gap-2`}>
        {(poll.options || []).map((o) => {
          const pct = total ? Math.round(((o.votes || 0) / total) * 100) : 0;
          if (!voted) {
            return (
              <button
                key={o.id}
                type="button"
                onClick={() => vote(o.id)}
                data-testid={`home-poll-opt-${o.id}`}
                className="text-left border-2 border-gray-100 rounded-lg p-2 hover:border-[#e11d2a] transition-colors font-medium text-sm"
              >
                {o.image && (
                  <div className="aspect-square rounded overflow-hidden mb-2 bg-gray-50">
                    <img src={o.image} alt={o.text} className="w-full h-full object-cover" />
                  </div>
                )}
                <div className="line-clamp-2">{o.text}</div>
              </button>
            );
          }
          return (
            <div
              key={o.id}
              className="relative border border-gray-100 rounded-lg p-2 overflow-hidden"
              data-testid={`home-poll-result-${o.id}`}
            >
              <div
                className="absolute inset-y-0 left-0 bg-[#e11d2a]/10"
                style={{ width: `${pct}%` }}
              />
              {o.image && (
                <div className="aspect-square rounded overflow-hidden mb-2 bg-gray-50 relative">
                  <img src={o.image} alt={o.text} className="w-full h-full object-cover" />
                </div>
              )}
              <div className="relative flex justify-between items-center gap-2">
                <span className="font-medium text-sm truncate">
                  {o.text}
                  {myOpt === o.id && (
                    <CheckCircle className="inline w-3.5 h-3.5 text-[#e11d2a] ml-1" />
                  )}
                </span>
                <span className="text-sm font-bold shrink-0">{pct}%</span>
              </div>
            </div>
          );
        })}
      </div>

      <div className="text-xs text-gray-500 mt-3">Toplam {total} oy</div>
      {showResults && null}
    </div>
  );
}
